import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { formatTotalTime } from "sj-ui-toolkit/src/utils/dates";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface UserName {
  givenName?: string | null;
  familyName?: string | null;
}

interface UserStats {
  totalPoints?: number | null;
  lastMonthPoints?: number | null;
  totalTime?: number | null;
  lastMonthTime?: number | null;
}

export interface UserProfileHeaderProps {
  user?: { name?: UserName | null; email?: string | null } | null;
  impersonating?: string | null;
  trialMode?: boolean;
  isSjAdmin?: boolean;
  handleImpersonateClick?: () => void;
  isLoading?: boolean;
  stats?: UserStats | null;
  level?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function UserProfileHeader({
  user,
  impersonating,
  trialMode,
  isSjAdmin,
  handleImpersonateClick,
  isLoading,
  stats,
  level,
}: UserProfileHeaderProps) {
  const name = `${user?.name?.givenName ?? ""} ${user?.name?.familyName ?? ""}`.trim() || "User";
  const hideTimeSpent = useHideTimeSpent();

  const points = stats?.totalPoints ?? 0;
  const timeSpent = stats?.totalTime ? formatTotalTime(stats.totalTime) : "0h 0m";
  const lastMonthPoints = stats?.lastMonthPoints ?? 0;
  const lastMonthTime = stats?.lastMonthTime ? formatTotalTime(stats.lastMonthTime) : "0h 0m";

  return (
    <div className="flex justify-between mb-8">
      <div className="flex items-center gap-4">
        <Avatar
          icon="pi pi-user"
          size="xlarge"
          style={{ backgroundColor: "var(--primary-color)", color: "var(--surface-0)" }}
          shape="circle"
        />
        <div>
          {isLoading ? (
            <>
              <Skeleton width="200px" height="2rem" className="mb-2" />
              <Skeleton width="250px" height="1.5rem" />
            </>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <h1 aria-label={`Profile of ${name}`}>{name}</h1>
                {!trialMode && !impersonating && isSjAdmin && (
                  <Button
                    label="Impersonate"
                    icon="pi pi-user"
                    onClick={handleImpersonateClick}
                    severity="secondary"
                  />
                )}
              </div>
              <h4>{level}</h4>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-[60px] flex-wrap">
        <div className="flex flex-col min-w-[150px]">
          <p className={`atlas-h5 ${themeClasses.textGray600}`}>TOTAL POINTS</p>
          <div className="atlas-h2">
            {isLoading ? <Skeleton width="75%" /> : <strong>{points}</strong>}
          </div>
          <div className={`atlas-h5 ${themeClasses.textGray600}`}>
            {isLoading ? (
              <Skeleton width="90%" />
            ) : (
              `${lastMonthPoints > 0 ? "+" : ""}${lastMonthPoints} points in the last 30 days`
            )}
          </div>
        </div>

        {!hideTimeSpent && (
          <div className="flex flex-col min-w-[150px]">
            <p className={`atlas-h5 ${themeClasses.textGray600}`}>TOTAL TIME ON THE PLATFORM</p>
            <span className="atlas-h2">
              {isLoading ? <Skeleton width="75%" /> : <strong>{timeSpent}</strong>}
            </span>
            <p className={`atlas-h5 ${themeClasses.textGray600}`}>
              {isLoading ? <Skeleton width="90%" /> : `${lastMonthTime} in the last 30 days`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
